import { useEffect, useId, useRef, useState } from "react";
import { Link, useLocation, useMatch } from "react-router-dom";
import { CircleChevronDown, LogOut, SquareCheck, Square, UserRound } from "lucide-react";
import { companyName, useCompanies, useCompany } from "../../lib/queries/companies";
import type { User } from "../../auth/queries";
import styles from "./SidebarHeader.module.css";

/** Sections that exist in every company; a switch keeps the user in theirs. */
const SECTIONS = ["invoices", "received-invoices", "credit-notes", "quotes", "receipts", "contacts", "products", "company"];

/**
 * The legacy sidebar's head (legacy/app/src/ui/layout/Sidebar.tsx UserInfo):
 * the current company in capitals and the user under it. The whole row is
 * the toggle; it unfolds a menu in place, inside the sidebar, pushing the
 * navigation down: the companies (only when there is more than one), the
 * user's account and the logout.
 *
 * One deliberate difference: picking a company keeps the current section
 * (legacy always opened the company's detail).
 */
export function SidebarHeader({ user, onLogout }: { user: User; onLogout: () => void }) {
  const companies = useCompanies();
  const match = useMatch("/c/:companyId/*");
  const currentId = match?.params.companyId;
  const current = useCompany(currentId);
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const section = match?.params["*"]?.split("/")[0] ?? "";
  const target = SECTIONS.includes(section) ? section : "invoices";
  const list = companies.data ?? [];

  // Any navigation (a pick here or a link elsewhere) folds the menu away.
  useEffect(() => setOpen(false), [pathname]);

  // A folded menu stays mounted so it can animate shut; `inert` takes its
  // links out of the tab order and the accessibility tree meanwhile. Set on
  // the DOM: React 18 does not know the attribute.
  useEffect(() => {
    panelRef.current?.toggleAttribute("inert", !open);
  }, [open]);

  return (
    <div className={styles.root}>
      <button
        type="button"
        data-composed-control
        className={styles.header}
        aria-expanded={open}
        aria-controls={panelId}
        data-testid="company-switcher"
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.text}>
          <span className={styles.company}>{current.data ? companyName(current.data) : "Faktio"}</span>
          <span className={styles.user}>{user.username}</span>
        </span>
        <CircleChevronDown className={styles.toggle} size={22} aria-hidden="true" />
      </button>
      <div ref={panelRef} id={panelId} className={styles.panel} data-open={open || undefined}>
        <div className={styles.panelInner}>
          {list.length > 1 && (
            <>
              <div className={styles.separator} />
              <div className={styles.title}>Firmy</div>
              {list.map((c) => {
                const Icon = c.id === currentId ? SquareCheck : Square;
                return (
                  <Link
                    key={c.id}
                    to={`/c/${c.id}/${target}`}
                    className={styles.item}
                    aria-current={c.id === currentId ? "true" : undefined}
                    onClick={() => setOpen(false)}
                  >
                    <Icon size={18} aria-hidden="true" />
                    <span>{companyName(c)}</span>
                  </Link>
                );
              })}
            </>
          )}
          <div className={styles.separator} />
          <Link to="/settings" className={styles.item} onClick={() => setOpen(false)}>
            <UserRound size={18} aria-hidden="true" />
            <span>Můj profil</span>
          </Link>
          <button type="button" data-composed-control className={styles.item} onClick={onLogout}>
            <LogOut size={18} aria-hidden="true" />
            <span>Odhlásit</span>
          </button>
        </div>
      </div>
    </div>
  );
}
